import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  Send,
  Wallet,
  ArrowRight,
  AlertCircle,
  CheckCircle,
  Shield,
  RefreshCw,
  Clock,
  CreditCard,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';
import {
  checkBalance,
  transferCardToMobileMoney,
} from '../../services/transferService';
import { cardService, CardWallet } from '../../services/cardService';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);

type Operator = 'mpesa' | 'orange' | 'airtel' | 'afrimoney';
type WalletAction = 'deposit' | 'withdrawal';

interface FormState {
  currency: 'USD' | 'CDF';
  to_method: Operator;
  to_action: WalletAction;
  amount: string;
  note: string;
}

interface TargetBalance {
  depositBalance: number;
  withdrawalBalance: number;
}

const OPERATORS: { id: Operator; name: string; color: string }[] = [
  { id: 'mpesa', name: 'M-Pesa', color: '#E60000' },
  { id: 'orange', name: 'Orange', color: '#FF8C00' },
  { id: 'airtel', name: 'Airtel', color: '#E53E3E' },
  { id: 'afrimoney', name: 'Afrimoney', color: '#6B46C1' },
];

const CardToMobile: React.FC = () => {
  const { t, formatCurrency } = useLocalization();
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  // Card wallets (production / live only)
  const [cardWallets, setCardWallets] = useState<CardWallet[]>([]);
  const [loadingCards, setLoadingCards] = useState(false);

  // Target operator wallet balances
  const [targetBalances, setTargetBalances] = useState<TargetBalance>({
    depositBalance: 0,
    withdrawalBalance: 0,
  });
  const [loadingTarget, setLoadingTarget] = useState(false);

  const [formData, setFormData] = useState<FormState>({
    currency: 'USD',
    to_method: 'mpesa',
    to_action: 'deposit',
    amount: '',
    note: 'Card to mobile money transfer',
  });

  const liveCards = useMemo(
    () => cardWallets.filter((w) => w.mode === 'production'),
    [cardWallets]
  );

  const sourceCard = useMemo(
    () => liveCards.find((w) => w.currency === formData.currency),
    [liveCards, formData.currency]
  );

  const sourceBalance = sourceCard?.balance ?? 0;
  const cardLast4 = sourceCard?.virtual_card_number
    ? sourceCard.virtual_card_number.slice(-4)
    : '----';

  const fetchCardWallets = useCallback(async () => {
    try {
      setLoadingCards(true);
      const res = await cardService.getWalletBalances();
      setCardWallets(res.wallets || []);
    } catch (err) {
      console.error('Failed to load card wallets:', err);
      setMessage({ type: 'error', text: t('transfers.cardToMobile.loadCardError') || 'Impossible de charger les portefeuilles carte' });
    } finally {
      setLoadingCards(false);
    }
  }, [t]);

  const fetchTargetBalance = useCallback(async () => {
    if (!user?.merchant_code) return;
    try {
      setLoadingTarget(true);
      const res = await checkBalance(formData.to_method, formData.currency);
      setTargetBalances({
        depositBalance: parseFloat(res.realtime_deposit) || 0,
        withdrawalBalance: parseFloat(res.realtime_withdrawal) || 0,
      });
    } catch (err) {
      console.error('Failed to load target balance:', err);
    } finally {
      setLoadingTarget(false);
    }
  }, [user?.merchant_code, formData.to_method, formData.currency]);

  useEffect(() => {
    fetchCardWallets();
  }, [fetchCardWallets]);

  useEffect(() => {
    fetchTargetBalance();
  }, [fetchTargetBalance]);

  // If the chosen currency has no live card available, switch to one that does
  useEffect(() => {
    if (liveCards.length === 0) return;
    if (!liveCards.find((w) => w.currency === formData.currency)) {
      const fallback = liveCards[0];
      setFormData((prev) => ({ ...prev, currency: fallback.currency as 'USD' | 'CDF' }));
    }
  }, [liveCards, formData.currency]);

  const targetOperator = OPERATORS.find((op) => op.id === formData.to_method);
  const transferAmount = parseFloat(formData.amount || '0');
  const exceedsBalance = transferAmount > sourceBalance;

  const getWalletLabel = (action: WalletAction) =>
    action === 'deposit' ? 'Pay-in Wallet' : 'Pay-out Wallet';

  const getWalletIcon = (action: WalletAction) =>
    action === 'deposit' ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user?.merchant_code) {
      setMessage({ type: 'error', text: 'Merchant code not available' });
      return;
    }
    if (transferAmount <= 0) {
      setMessage({ type: 'error', text: t('transfers.cardToMobile.invalidAmount') || 'Le montant doit être supérieur à 0' });
      return;
    }
    if (exceedsBalance) {
      setMessage({ type: 'error', text: t('transfers.cardToMobile.exceeds') || 'Montant supérieur au solde disponible' });
      return;
    }
    if (!sourceCard) {
      setMessage({ type: 'error', text: t('transfers.cardToMobile.noCard') || `Aucune carte ${formData.currency} disponible` });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const res = await transferCardToMobileMoney({
        amount: transferAmount,
        currency: formData.currency,
        to_method: formData.to_method,
        to_action: formData.to_action,
        note: formData.note.trim() || undefined,
      });

      const isPending = res?.status === 'PENDING';
      const isSuccess = res?.status === 'SUCCESS';

      MySwal.fire({
        title: isSuccess
          ? '✅ Transfer completed'
          : isPending
          ? '⏳ Transfer pending'
          : `Transfer status: ${res?.status || 'UNKNOWN'}`,
        html: `
          <div style="text-align: left;">
            <div style="background-color: #f3f4f6; padding: 1rem; border-radius: 0.5rem; margin-bottom: 1rem;">
              <div style="display: grid; grid-template-columns: auto 1fr; gap: 0.5rem 1rem; font-size: 0.875rem;">
                <div style="color: #4b5563;">Reference:</div>
                <div style="font-family: monospace; color: #111827;">${res?.reference || '-'}</div>
                <div style="color: #4b5563;">Amount:</div>
                <div style="font-weight: 600; color: #111827;">${transferAmount.toLocaleString()} ${formData.currency}</div>
                <div style="color: #4b5563;">From:</div>
                <div style="color: #111827;">Card · ****${cardLast4}</div>
                <div style="color: #4b5563;">To:</div>
                <div style="color: #111827;">${targetOperator?.name} · ${getWalletLabel(formData.to_action)}</div>
              </div>
            </div>
            ${
              res?.message
                ? `<p style="font-size: 0.75rem; color: #6b7280;">${res.message}</p>`
                : ''
            }
          </div>
        `,
        icon: isSuccess ? 'success' : isPending ? 'info' : 'warning',
        confirmButtonText: 'Done',
        confirmButtonColor: '#10b981',
        width: '500px',
        customClass: { popup: 'rounded-2xl', confirmButton: 'px-6 py-2 rounded-lg' },
      });

      setMessage({
        type: isSuccess ? 'success' : 'info',
        text: res?.message || `Transfer ${res?.status?.toLowerCase()} · Reference: ${res?.reference}`,
      });

      setFormData((prev) => ({ ...prev, amount: '', note: 'Card to mobile money transfer' }));
      fetchCardWallets();
      fetchTargetBalance();
    } catch (error: any) {
      console.error('Card-to-mobile transfer error:', error);
      let errorMessage = 'Error processing transfer';
      const detail = error?.response?.data?.detail;
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        errorMessage = detail.map((d: any) => d.msg || d).join(' · ');
      } else if (detail?.message) {
        errorMessage = detail.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      MySwal.fire({
        title: '❌ Transfer Failed',
        html: `
          <div style="text-align: left;">
            <p style="color: #dc2626; font-weight: 600; margin-bottom: 1rem;">
              Transfer could not be processed
            </p>
            <div style="background-color: #fef2f2; padding: 0.75rem; border-radius: 0.5rem;">
              <p style="color: #991b1b; font-size: 0.875rem;">${errorMessage}</p>
            </div>
          </div>
        `,
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#E53E3E',
        width: '500px',
        customClass: { popup: 'rounded-2xl', confirmButton: 'px-6 py-2 rounded-lg' },
      });

      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const availableCurrencies: Array<'USD' | 'CDF'> = useMemo(() => {
    const set = new Set<'USD' | 'CDF'>();
    liveCards.forEach((w) => {
      if (w.currency === 'USD' || w.currency === 'CDF') set.add(w.currency);
    });
    return Array.from(set);
  }, [liveCards]);

  return (
    <div className="relative max-w-4xl mx-auto p-4">
      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-8 shadow-2xl max-w-md w-full mx-4">
            <div className="flex flex-col items-center">
              <div className="relative w-20 h-20 mb-6">
                <div className="absolute inset-0 border-4 border-emerald-200 rounded-full"></div>
                <div className="absolute inset-0 border-4 border-emerald-600 rounded-full border-t-transparent animate-spin"></div>
                <RefreshCw className="absolute inset-1/4 w-10 h-10 text-emerald-600 animate-pulse" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Processing Transfer</h3>
              <p className="text-gray-600 text-center mb-6">
                Transferring {transferAmount} {formData.currency}...
              </p>
              <div className="bg-gray-50 rounded-lg p-4 w-full mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center mr-2 text-white bg-slate-900">
                      <CreditCard className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-700">Virtual card</p>
                      <p className="text-xs text-gray-500">****{cardLast4}</p>
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-gray-400 mx-2" />
                  <div className="flex items-center">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center mr-2 text-white"
                      style={{ backgroundColor: targetOperator?.color }}
                    >
                      {getWalletIcon(formData.to_action)}
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-700">{getWalletLabel(formData.to_action)}</p>
                      <p className="text-xs text-gray-500">{targetOperator?.name}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                <div className="bg-gradient-to-r from-emerald-600 to-teal-600 h-2 rounded-full animate-pulse"></div>
              </div>
              <p className="text-xs text-gray-500">Debiting card and crediting mobile money wallet...</p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl p-6 mb-6 text-white">
        <div className="flex items-center">
          <div className="bg-white bg-opacity-20 p-3 rounded-lg mr-4">
            <CreditCard className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold mb-1">{t('transfers.cardToMobile.title') || 'Card to Mobile Money'}</h1>
            <p className="text-white text-opacity-90 text-sm">
              {t('transfers.cardToMobile.subtitle') || 'Transfer funds from your virtual card to a mobile money operator'}
            </p>
          </div>
        </div>
      </div>

      {/* Alert */}
      {message && !loading && (
        <div
          className={`p-3 rounded-lg mb-4 flex items-center ${
            message.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : message.type === 'info'
              ? 'bg-yellow-50 border border-yellow-200 text-yellow-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle className="h-4 w-4 mr-2 flex-shrink-0" />
          ) : message.type === 'info' ? (
            <Clock className="h-4 w-4 mr-2 flex-shrink-0" />
          ) : (
            <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
          )}
          <span className="text-sm font-medium">{message.text}</span>
        </div>
      )}

      {/* Balance cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* Source card */}
        <div className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-700">{t('transfers.cardToMobile.sourceCard') || 'Source — Virtual Card'}</h3>
              <p className="text-xs text-gray-500">****{cardLast4} · LIVE · {formData.currency}</p>
            </div>
            <div className="w-9 h-9 rounded-lg bg-slate-900 flex items-center justify-center text-white">
              <CreditCard className="h-4 w-4" />
            </div>
          </div>
          {loadingCards ? (
            <div className="h-10 flex items-center">
              <div className="w-32 h-4 bg-gray-200 rounded animate-pulse" />
            </div>
          ) : (
            <div>
              <p className="text-xs text-gray-500">{t('balances.available') || 'Disponible'}</p>
              <p className="text-2xl font-bold text-gray-900 tabular-nums">
                {formatCurrency(sourceBalance, formData.currency)}
              </p>
              {!sourceCard && (
                <p className="text-xs text-amber-600 font-medium mt-1">
                  {t('transfers.cardToMobile.noCard') || `Aucune carte ${formData.currency} disponible`}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Target operator */}
        <div className="bg-white rounded-lg p-4 border border-emerald-200 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-700">{t('transfers.cardToMobile.targetOperator') || 'Target — Mobile Money'}</h3>
              <p className="text-xs text-gray-500">{targetOperator?.name}</p>
            </div>
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center text-white"
              style={{ backgroundColor: targetOperator?.color }}
            >
              {getWalletIcon(formData.to_action)}
            </div>
          </div>
          {loadingTarget ? (
            <div className="h-10 flex items-center">
              <div className="w-32 h-4 bg-gray-200 rounded animate-pulse" />
            </div>
          ) : (
            <div className="space-y-1">
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Pay-in Wallet</span>
                <span className="font-semibold text-gray-900">
                  {formatCurrency(targetBalances.depositBalance, formData.currency)}
                </span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Pay-out Wallet</span>
                <span className="font-semibold text-gray-900">
                  {formatCurrency(targetBalances.withdrawalBalance, formData.currency)}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit} className={`space-y-6 ${loading ? 'opacity-50' : ''}`}>
        {/* Configuration */}
        <div className="bg-white rounded-lg p-5 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Wallet className="h-4 w-4 mr-2 text-emerald-600" />
            {t('transfers.cardToMobile.configuration') || 'Transfer Configuration'}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('common.currency') || 'Currency'}
              </label>
              <select
                value={formData.currency}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, currency: e.target.value as 'USD' | 'CDF' }))
                }
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                required
                disabled={loading}
              >
                {availableCurrencies.length === 0 ? (
                  <>
                    <option value="USD">USD</option>
                    <option value="CDF">CDF</option>
                  </>
                ) : (
                  availableCurrencies.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))
                )}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('transfers.cardToMobile.targetOperatorLabel') || 'Target Operator'}
              </label>
              <select
                value={formData.to_method}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, to_method: e.target.value as Operator }))
                }
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                required
                disabled={loading}
              >
                {OPERATORS.map((op) => (
                  <option key={op.id} value={op.id}>
                    {op.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('transfers.cardToMobile.targetWallet') || 'Target Wallet'}
              </label>
              <div className="grid grid-cols-2 gap-2">
                {(['deposit', 'withdrawal'] as WalletAction[]).map((a) => {
                  const selected = formData.to_action === a;
                  return (
                    <button
                      type="button"
                      key={a}
                      onClick={() => setFormData((p) => ({ ...p, to_action: a }))}
                      disabled={loading}
                      className={`text-left px-3 py-3 rounded-xl border transition-all ${
                        selected
                          ? 'border-emerald-600 bg-emerald-50'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                    >
                      <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                        {getWalletIcon(a)}
                        {getWalletLabel(a)}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5 leading-snug">
                        {a === 'deposit'
                          ? (t('transfers.cardToMobile.depositHint') || 'Crédité sur le wallet Pay-in (collection)')
                          : (t('transfers.cardToMobile.withdrawalHint') || 'Crédité sur le wallet Pay-out (cashout)')}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Amount + note */}
        <div className="bg-white rounded-lg p-5 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Send className="h-4 w-4 mr-2 text-emerald-600" />
            {t('transfers.cardToMobile.details') || 'Transfer Details'}
          </h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('transfers.cardToMobile.amount') || 'Transfer Amount'}
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  max={sourceBalance}
                  className="w-full pl-3 pr-20 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent tabular-nums"
                  required
                  disabled={loading || !sourceCard}
                />
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, amount: sourceBalance.toFixed(2) })}
                  disabled={loading || !sourceCard}
                  className="absolute right-2 top-1/2 -translate-y-1/2 px-2.5 py-1 rounded-md bg-emerald-50 hover:bg-emerald-100 text-xs font-semibold text-emerald-700 disabled:opacity-50"
                >
                  Max
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {t('transfers.cardToMobile.availableOnCard') || 'Available on card'}: {formatCurrency(sourceBalance, formData.currency)}
              </p>
              {exceedsBalance && (
                <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5" />
                  {t('transfers.cardToMobile.exceeds') || 'Montant supérieur au solde de la carte'}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('transfers.note') || 'Note (Optional)'}
              </label>
              <input
                type="text"
                value={formData.note}
                onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                placeholder={t('transfers.cardToMobile.notePlaceholder') || 'e.g. Settle weekly card receipts'}
                maxLength={120}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                disabled={loading}
              />
            </div>
          </div>
        </div>

        {/* Summary */}
        {transferAmount > 0 && !loading && sourceCard && (
          <div className="bg-emerald-50 rounded-lg p-5 border border-emerald-200">
            <h3 className="text-lg font-semibold text-emerald-900 mb-4">
              {t('transfers.cardToMobile.summary') || 'Transfer Summary'}
            </h3>

            <div className="flex items-center justify-between mb-4 p-4 bg-white rounded-lg border border-emerald-100">
              <div className="flex items-center text-center flex-1">
                <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center mr-3 text-white">
                  <CreditCard className="h-4 w-4" />
                </div>
                <div className="text-left">
                  <p className="font-medium text-gray-900 text-sm">Virtual Card</p>
                  <p className="text-xs text-gray-600">****{cardLast4}</p>
                  <p className="text-xs text-red-600 font-medium">
                    - {transferAmount.toLocaleString()} {formData.currency}
                  </p>
                </div>
              </div>

              <ArrowRight className="h-5 w-5 text-emerald-600 mx-3 flex-shrink-0" />

              <div className="flex items-center text-center flex-1">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center mr-3 text-white"
                  style={{ backgroundColor: targetOperator?.color }}
                >
                  {getWalletIcon(formData.to_action)}
                </div>
                <div className="text-left">
                  <p className="font-medium text-gray-900 text-sm">{getWalletLabel(formData.to_action)}</p>
                  <p className="text-xs text-gray-600">{targetOperator?.name}</p>
                  <p className="text-xs text-green-600 font-medium">
                    + {transferAmount.toLocaleString()} {formData.currency}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-2 text-sm bg-white rounded-lg p-4 border border-emerald-100">
              <div className="flex justify-between items-center py-1">
                <span className="text-gray-600">Amount</span>
                <span className="font-semibold text-gray-900">
                  {transferAmount.toLocaleString()} {formData.currency}
                </span>
              </div>
              <div className="flex justify-between items-center py-1">
                <span className="text-gray-600">From</span>
                <span className="font-medium text-slate-900">Card · ****{cardLast4}</span>
              </div>
              <div className="flex justify-between items-center py-1">
                <span className="text-gray-600">To</span>
                <span className="font-medium text-emerald-700">
                  {targetOperator?.name} · {getWalletLabel(formData.to_action)}
                </span>
              </div>
              <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                <span className="font-medium text-emerald-900">Status</span>
                <span className="px-2 py-1 rounded text-xs font-medium bg-emerald-100 text-emerald-800">
                  Ready to Process
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Submit */}
        <div className="bg-white rounded-lg p-5 border border-gray-200">
          <button
            type="submit"
            disabled={loading || !sourceCard || transferAmount <= 0 || exceedsBalance}
            className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                {t('transfers.cardToMobile.processing') || 'Processing Transfer...'}
              </div>
            ) : (
              t('transfers.cardToMobile.confirm') || 'Confirm Transfer'
            )}
          </button>

          <div className="text-center mt-3">
            <p className="text-xs text-gray-500 flex items-center justify-center">
              <Shield className="h-3 w-3 mr-1" />
              {t('transfers.cardToMobile.securityHint') || 'Card is debited atomically; mobile money wallet is credited on success'}
            </p>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CardToMobile;
